import { X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { type AskAiResponse, askAi } from '../lib/api';

/**
 * Global slide-over Ask AI panel. Opens with `?` or `/` keyboard shortcut and
 * closes with Escape.
 */
export function AskAiPanel(): ReactElement {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<AskAiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (!inEditable && (e.key === '?' || e.key === '/')) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return <></>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await askAi(question));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-card shadow-lg"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-base font-medium">Ask AI</h2>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close">
          <X className="size-4" strokeWidth={1.75} />
        </button>
      </header>
      <form onSubmit={submit} className="border-b border-border p-4">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. How many bookings did we have last week?"
          className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
          rows={3}
          autoFocus
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !question.trim()}
          className="mt-2 w-full rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {loading ? 'Thinking…' : 'Ask'}
        </button>
      </form>
      <div className="flex-1 overflow-y-auto p-4 text-sm">
        {error && <p className="text-red-700">{error}</p>}
        {result && (
          <>
            <p className="whitespace-pre-wrap">{result.answer}</p>
            {result.toolCallCount > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Used {result.toolCallCount} tool(s):{' '}
                {result.toolCalls.map((t) => t.name).join(', ')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
